import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import {
    DashboardComparisonOptionView,
    DivisionView,
    GetOpportunityStatsByPeriodResultOpportunityTotalsResultDto,
    SearchDivisionsParameters,
    SearchUsersParameters,
    UserView
} from '@api-proxies';
import { IUserOrganization } from '@app/interfaces/user-organization.interface';
import { DepartmentService } from '@http/clients/department.service';
import { OpportunityDashboardService } from '@http/clients/opportunity-dashboard.service';
import { UserService } from '@http/clients/user.service';
import { IUser } from '@interfaces/user.interface';
import { DashboardPeriodOptions } from '@models/enums/DashboardPeriodOptions';
import { UserRoles } from '@models/enums/UserRoles';
import { AwaiterService } from '@services/awaiter.service';
import { UserOrganizationStorageService } from '@services/user-organization-storage.service';
import { UserStorageService } from '@services/user-storage.service';
import {
    BidVsAwardedChartComponent,
    IBidVsAwardedChartConfig
} from '@shared/charts/bid-vs-awarded-chart/bid-vs-awarded-chart.component';
import {
    ISalesFunnelChartConfig,
    SalesFunnelChartComponent
} from '@shared/charts/sales-funnel-chart/sales-funnel-chart.component';
import {
    GenericDropdownComponent,
    GenericDropdownMultiselectComponent,
    GenericTypeAheadMultiselectComponent,
    IconSanitizerService,
    SkeletonComponent,
    SkeletonConfig,
    StorageService
} from 'ngx-lib';
import { CustomMenuItemComponent } from '../../shared/custom-menu-item/custom-menu-item.component';
import { DashboardCardComponent } from './dashboard-card/dashboard-card.component';
import { DashboardOpportunitiesGridComponent } from './dashboard-opportunities-grid/dashboard-opportunities-grid.component';
import { DashboardStatComponent } from './dashboard-stat/dashboard-stat.component';

interface IFilterValueDashboard {
    period?: DashboardComparisonOptionView;
    departments?: DivisionView[];
    salespersons?: UserView[];
}

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    imports: [
        CommonModule,
        GenericDropdownComponent,
        GenericDropdownMultiselectComponent,
        GenericTypeAheadMultiselectComponent,
        SkeletonComponent,
        DashboardCardComponent,
        DashboardStatComponent,
        SalesFunnelChartComponent,
        BidVsAwardedChartComponent,
        DashboardOpportunitiesGridComponent,
        CustomMenuItemComponent
    ]
})
export class DashboardComponent implements OnInit {
    private readonly router: Router = inject(Router);
    private readonly awaiter: AwaiterService = inject(AwaiterService);
    private readonly opportunityDashboardService: OpportunityDashboardService = inject(OpportunityDashboardService);
    private readonly departmentService: DepartmentService = inject(DepartmentService);
    private readonly userService: UserService = inject(UserService);
    private readonly iconSanitizer: IconSanitizerService = inject(IconSanitizerService);
    private readonly storageService: StorageService = inject(StorageService);
    private readonly userOrganizationStorageService: UserOrganizationStorageService =
        inject(UserOrganizationStorageService);
    private readonly userStorageService: UserStorageService = inject(UserStorageService);

    comparisonOptions = signal<DashboardComparisonOptionView[] | undefined>(undefined);
    departments = signal<DivisionView[]>([]);
    salespersons = signal<UserView[]>([]);
    opportunityStat = signal<GetOpportunityStatsByPeriodResultOpportunityTotalsResultDto | undefined>(undefined);
    salesFunnelChartConfig = signal<ISalesFunnelChartConfig | undefined>(undefined);
    bidVsAwardedChartConfig = signal<IBidVsAwardedChartConfig | undefined>(undefined);

    activeDepartments = computed(() => this.departments().filter((d) => d.isActive));

    filters: IFilterValueDashboard = {
        period: undefined,
        departments: undefined,
        salespersons: undefined
    };

    currentUser = signal<IUser | undefined>(undefined);
    currentAuthenticatedUser = signal<UserView | undefined>(undefined);

    infoIcon = signal<SafeHtml | undefined>(undefined);
    plusIcon = signal<SafeHtml | undefined>(undefined);

    skeleton = signal<boolean>(true);
    rectangleSkeletonConfig: SkeletonConfig = {
        count: 1,
        type: 'rectangle',
        hasBox: false,
        width: '100%',
        height: '252px',
        boxStyle: {
            backgroundColor: '#ffffff',
            border: '1px solid #98b7d2',
            borderRadius: '8px',
            padding: '1rem',
            width: '100%',
            height: '252px'
        },
        rectangleStyle: {
            width: '100%',
            height: '100%',
            borderRadius: '8px'
        }
    };

    TextSkeletonConfig: SkeletonConfig = {
        count: 2,
        type: 'text',
        hasBox: false,
        width: '50%'
    };

    private currentOrganizationId?: number;
    private currentUserId?: number;
    private organizationUserData?: IUserOrganization;

    async ngOnInit(): Promise<void> {
        this.skeleton.set(true);

        this.infoIcon.set(this.iconSanitizer.getIcon('info'));
        this.plusIcon.set(this.iconSanitizer.getIcon('plus'));

        await this.awaiter.awaitAction('Getting Filters Options', async () => {
            this.comparisonOptions.set(await this.opportunityDashboardService.getComparisonOptions());
            this.departments.set(await this.departmentService.search({} as SearchDivisionsParameters));
            this.currentAuthenticatedUser.set(await this.userService.getAuthenticatedUser());
        });

        this.organizationUserData = this.userOrganizationStorageService.getUserOrganization();
        this.currentOrganizationId = this.organizationUserData?.id;

        const user = this.userStorageService.getUser();
        this.currentUser.set(user);
        this.currentUserId = user?.id;

        const filterSaved = this.getDashboardFiltersFromStorage(this.currentUserId, this.currentOrganizationId);

        if (filterSaved?.departments?.length) {
            const departmentMap = new Map(this.departments().map((d) => [d.id, d]));
            filterSaved.departments = filterSaved.departments.map((saved) => departmentMap.get(saved.id) ?? saved);
        }

        this.saveDashboardFiltersToStorage(this.currentUserId, this.currentOrganizationId, filterSaved);

        const period =
            filterSaved?.period ?? this.comparisonOptions()?.find((x) => x.id === DashboardPeriodOptions.Last365Days);

        this.filters.period = period;
        this.filters.departments = filterSaved?.departments;

        if (filterSaved?.salespersons?.length) {
            this.filters.salespersons = filterSaved.salespersons;
            this.salespersons.set(filterSaved.salespersons);
        } else {
            await this.setDefaultSalespersons();
        }

        await this.loadDashboardData();

        this.skeleton.set(false);
    }

    async onFilterChange(): Promise<void> {
        this.skeleton.set(true);

        if (this.filters.salespersons) {
            this.salespersons.set(this.filters.salespersons);
        }

        const period =
            this.filters?.period?.id ??
            this.comparisonOptions()?.find((x) => x.id === DashboardPeriodOptions.Last365Days)?.id;
        const departmentIds = this.filters?.departments?.map((d) => d.id);
        const salesPersonIds = this.filters?.salespersons?.map((s) => s.externalId ?? s.id);
        this.saveDashboardFiltersToStorage(this.currentUserId, this.currentOrganizationId, this.filters);

        if (period) {
            await this.getOpportunityStat(period, departmentIds, salesPersonIds);
            this.buildChartsConfig();
        }
        this.skeleton.set(false);
    }

    async getOpportunityStat(comparisonId: number, departmentIds?: number[], salesPersonIds?: number[]): Promise<void> {
        const stat = await this.opportunityDashboardService.getOpportunityStats(
            comparisonId,
            departmentIds,
            salesPersonIds
        );
        this.opportunityStat.set(stat);
    }

    onCreateNewOpportunityClicked() {
        this.router.navigate([`/opportunities-create`]);
    }

    async onSalespersonTextChange(filterText: string): Promise<void> {
        if (filterText && filterText.trim()) {
            const results = await this.userService.search({
                filterText: filterText.trim(),
                isActive: true
            } as SearchUsersParameters);
            this.salespersons.set(results);
        } else {
            this.salespersons.set([]);
        }
    }

    async onSalespersonsChange(): Promise<void> {
        await this.onFilterChange();
    }

    private async loadDashboardData(): Promise<void> {
        await this.onFilterChange();
    }

    private buildChartsConfig(): void {
        const stat = this.opportunityStat();
        if (!stat) return;

        this.salesFunnelChartConfig.set({
            title: 'Sales Funnel',
            labels: {
                current: stat?.dashboardComparisonOptionView?.currentPeriodDescription,
                previous: stat?.dashboardComparisonOptionView?.previousPeriodDescription
            },
            data: [
                {
                    category: 'Opportunities',
                    current: stat?.currentResult.opportunitiesAmount,
                    previous: stat?.previousResult.opportunitiesAmount
                },
                {
                    category: 'Bid',
                    current: stat.currentResult.bidAmount,
                    previous: stat.previousResult.bidAmount
                },
                {
                    category: 'Awarded',
                    current: stat.currentResult.awardedAmount,
                    previous: stat.previousResult.awardedAmount
                }
            ],
            descriptionHelpText: `
                ${this.createChartTooltipContent('Opportunities', 'Total potential value of opportunities created in the period.')}
                ${this.createChartTooltipContent('Bid', 'Total bid amount for estimates bid in the period.')}
                ${this.createChartTooltipContent('Awarded', 'Total awarded amount for estimates awarded in the period.')}
            `
        });

        this.bidVsAwardedChartConfig.set({
            title: 'Bid vs. Awarded',
            data: [
                {
                    period: 'Previous',
                    bidTotal: stat.previousResult.totalBidCount,
                    awardedTotal: stat.previousResult.totalAwardedCount,
                    awardedBid: stat.previousResult.awardedBidCount,
                    awardedRate: stat.previousResult.awardedRate
                },
                {
                    period: 'Current',
                    bidTotal: stat.currentResult.totalBidCount,
                    awardedTotal: stat.currentResult.totalAwardedCount,
                    awardedBid: stat.currentResult.awardedBidCount,
                    awardedRate: stat.currentResult.awardedRate
                }
            ],
            descriptionHelpText: `
                ${this.createChartTooltipContent('Bid Total', ' Number of estimates bid in the period.')}
                ${this.createChartTooltipContent('Awarded Bid', 'Number of estimates bid and awarded in the period.')}
                ${this.createChartTooltipContent('Awarded Total', 'Number of estimates awarded in the period.')}
                ${this.createChartTooltipContent('Awarded Rate', 'Awarded Bid / Bid Total')}
            `
        });
    }

    private createChartTooltipContent(title: string, description: string): string {
        return `
            <span>${title}:</span>
            <span class="tooltip-content">${description}</span><br />
        `;
    }

    private saveDashboardFiltersToStorage(
        userId?: number,
        organizationId?: number,
        filters?: IFilterValueDashboard
    ): void {
        if (!userId || !organizationId || !filters || !Object.keys(filters).length) return;
        const key = `dashboardFilters-user:${userId}-org:${organizationId}`;
        this.storageService.setItem(key, filters);
    }

    private getDashboardFiltersFromStorage(
        userId?: number,
        organizationId?: number
    ): IFilterValueDashboard | undefined {
        if (!userId || !organizationId) return;
        const key = `dashboardFilters-user:${userId}-org:${organizationId}`;
        return this.storageService.getItem(key);
    }

    private async setDefaultSalespersons(): Promise<void> {
        const authenticatedUser = this.currentAuthenticatedUser();
        if (!authenticatedUser || !this.organizationUserData) {
            this.filters.salespersons = undefined;
            return;
        }

        const userRole = this.organizationUserData.roleId;

        if (userRole === UserRoles.SalesPerson && authenticatedUser.externalId) {
            const currentSalesperson = await this.userService.getById(authenticatedUser.externalId);
            const salespersonsList = currentSalesperson ? [currentSalesperson] : undefined;
            this.filters.salespersons = salespersonsList;
            this.salespersons.set(salespersonsList || []);
        } else {
            this.filters.salespersons = undefined;
        }
    }
}
